import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Modal,
  Input,
  InputNumber,
  Select,
  Switch,
  message,
  Form,
  Divider,
} from 'antd';
import { PlusOutlined, EditOutlined, ApartmentOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import intercompanyService, {
  type ConsolidationGroupDetail,
  type ConsolidationGroupMember,
} from '@/services/intercompany.service';

const { Title, Paragraph } = Typography;

type MemberDraft = {
  key: string;
  member_company_id?: string;
  ownership_pct: number;
  consolidation_method: string;
  is_parent: boolean;
};

const emptyMember = (): MemberDraft => ({
  key: `${Date.now()}-${Math.random()}`,
  ownership_pct: 100,
  consolidation_method: 'Full',
  is_parent: false,
});

const IntercompanyGroupsPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ConsolidationGroupDetail | null>(null);
  const [groupCode, setGroupCode] = useState('');
  const [groupName, setGroupName] = useState('');
  const [notes, setNotes] = useState('');
  const [members, setMembers] = useState<MemberDraft[]>([emptyMember()]);

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['intercompany-groups'],
    queryFn: () => intercompanyService.listGroups(),
  });

  const { data: tenantCompanies = [] } = useQuery({
    queryKey: ['intercompany-tenant-companies'],
    queryFn: () => intercompanyService.listTenantCompanies(),
    enabled: modalOpen,
  });

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        group_code: groupCode,
        group_name: groupName,
        notes: notes || undefined,
        members: members
          .filter((m) => m.member_company_id)
          .map((m, idx) => ({
            member_company_id: m.member_company_id!,
            ownership_pct: m.ownership_pct,
            consolidation_method: m.consolidation_method,
            is_parent: m.is_parent,
            sort_order: idx,
          })),
      };
      if (editing) {
        return intercompanyService.updateGroup(editing.id, {
          group_name: groupName,
          notes: notes || undefined,
          members: payload.members,
        });
      }
      return intercompanyService.createGroup(payload);
    },
    onSuccess: () => {
      message.success(editing ? t('groupUpdated') : t('groupCreated'));
      setModalOpen(false);
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['intercompany-groups'] });
    },
    onError: (e: Error) => message.error(e.message || t('saveFailed')),
  });

  const openCreate = () => {
    setEditing(null);
    setGroupCode('');
    setGroupName('');
    setNotes('');
    setMembers([{ ...emptyMember(), is_parent: true }]);
    setModalOpen(true);
  };

  const openEdit = async (id: string) => {
    try {
      const detail = await intercompanyService.getGroup(id);
      setEditing(detail);
      setGroupCode(detail.group_code);
      setGroupName(detail.group_name);
      setNotes(detail.notes || '');
      setMembers(
        detail.members.map((m: ConsolidationGroupMember) => ({
          key: m.id,
          member_company_id: m.member_company_id,
          ownership_pct: Number(m.ownership_pct),
          consolidation_method: m.consolidation_method,
          is_parent: m.is_parent,
        }))
      );
      setModalOpen(true);
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('failedLoadGroup'));
    }
  };

  const companyOptions = useMemo(
    () =>
      tenantCompanies.map((c) => ({
        value: c.id,
        label: `${c.code} — ${c.name}`,
      })),
    [tenantCompanies]
  );

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t('consolidationGroupsTitle')}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('consolidationGroupsSubtitle')}
          </Paragraph>
        </div>
        <Space>
          <Button onClick={() => navigate('/fin/reports/intercompany-consolidation')}>
            {t('consolidationReports')}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t('newGroup')}
          </Button>
        </Space>
      </Space>

      <Card>
        <Table
          loading={isLoading}
          rowKey="id"
          dataSource={groups}
          columns={[
            { title: t('code'), dataIndex: 'group_code', width: 120 },
            { title: t('name'), dataIndex: 'group_name' },
            { title: t('currency'), dataIndex: 'reporting_currency', width: 100 },
            {
              title: t('active'),
              dataIndex: 'is_active',
              width: 90,
              render: (v: boolean) => (v ? tc('yes') : tc('no')),
            },
            {
              title: '',
              key: 'actions',
              width: 280,
              render: (_: unknown, r: { id: string; is_active: boolean }) => (
                <Space>
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r.id)}>
                    {tc('edit')}
                  </Button>
                  <Button
                    size="small"
                    icon={<ApartmentOutlined />}
                    onClick={() => navigate(`/fin/reports/intercompany-consolidation?group=${r.id}`)}
                  >
                    {t('reports')}
                  </Button>
                  {r.is_active && (
                    <Button
                      size="small"
                      danger
                      onClick={async () => {
                        try {
                          await intercompanyService.updateGroup(r.id, { is_active: false });
                          message.success(t('groupDeactivated'));
                          qc.invalidateQueries({ queryKey: ['intercompany-groups'] });
                        } catch (e) {
                          message.error(e instanceof Error ? e.message : t('failedGeneric'));
                        }
                      }}
                    >
                      {t('deactivate')}
                    </Button>
                  )}
                </Space>
              ),
            },
          ]}
          locale={{ emptyText: t('noConsolidationGroupsYet') }}
        />
      </Card>

      <Modal
        title={editing ? t('editConsolidationGroup') : t('newConsolidationGroup')}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={() => {
          if (!editing && !groupCode.trim()) {
            message.warning(t('groupCodeRequired'));
            return;
          }
          if (!groupName.trim()) {
            message.warning(t('groupNameRequired'));
            return;
          }
          if (!members.some((m) => m.member_company_id)) {
            message.warning(t('addAtLeastOneMember'));
            return;
          }
          saveMut.mutate();
        }}
        confirmLoading={saveMut.isPending}
        okText={editing ? tc('save') : tc('create')}
        width={720}
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label={t('groupCode')} required>
            <Input
              value={groupCode}
              disabled={!!editing}
              onChange={(e) => setGroupCode(e.target.value.toUpperCase())}
              placeholder={t('placeholderHolding')}
            />
          </Form.Item>
          <Form.Item label={t('groupName')} required>
            <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} />
          </Form.Item>
          <Form.Item label={t('notes')}>
            <Input.TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Form.Item>
          <Divider>{t('members')}</Divider>
          {members.map((m, idx) => (
            <Space key={m.key} align="start" style={{ display: 'flex', marginBottom: 12 }} wrap>
              <Select
                style={{ width: 260 }}
                placeholder={t('company')}
                value={m.member_company_id}
                options={companyOptions}
                showSearch
                optionFilterProp="label"
                onChange={(v) => {
                  const next = [...members];
                  next[idx] = { ...next[idx], member_company_id: v };
                  setMembers(next);
                }}
              />
              <InputNumber
                min={0}
                max={100}
                step={0.01}
                value={m.ownership_pct}
                addonAfter="%"
                style={{ width: 120 }}
                onChange={(v) => {
                  const next = [...members];
                  next[idx] = { ...next[idx], ownership_pct: Number(v ?? 100) };
                  setMembers(next);
                }}
              />
              <Select
                style={{ width: 140 }}
                value={m.consolidation_method}
                options={[
                  { value: 'Full', label: t('consolMethodFull') },
                  { value: 'Proportional', label: t('consolMethodProportional') },
                  { value: 'Equity', label: t('consolMethodEquity') },
                  { value: 'None', label: t('consolMethodNone') },
                ]}
                onChange={(v) => {
                  const next = [...members];
                  next[idx] = { ...next[idx], consolidation_method: v };
                  setMembers(next);
                }}
              />
              <span style={{ lineHeight: '32px' }}>
                {t('parentLabel')}{' '}
                <Switch
                  checked={m.is_parent}
                  onChange={(v) => {
                    const next = members.map((row, i) => ({
                      ...row,
                      is_parent: i === idx ? v : v ? false : row.is_parent,
                    }));
                    setMembers(next);
                  }}
                />
              </span>
              <Button
                danger
                type="link"
                disabled={members.length <= 1}
                onClick={() => setMembers(members.filter((_, i) => i !== idx))}
              >
                {tc('remove')}
              </Button>
            </Space>
          ))}
          <Button type="dashed" block icon={<PlusOutlined />} onClick={() => setMembers([...members, emptyMember()])}>
            {t('addMember')}
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default IntercompanyGroupsPage;
